/* eslint-disable max-lines */
// deps
import { useCallback, useContext } from "react";

// internal-deps
import {
    AnalyticsContext,
    ISuperSavingBottomWidgetSubscriptionDataProps,
    ICoupon,
    IResponseCartItem,
} from "@swiggy-private/checkout-core";

// interfaces
import {
    IOnImpressionBillDetailsProps,
    ImpressionDistanceAlert,
    IProceedtoPayClick,
    IBillEntries,
    IImpressionTurboCheckout,
    IClickTurboCheckout,
    IClickTurboPaymentMethodChange,
} from "@dineout-cart-client/interfaces/Cart";

// analytics
import {
    impressionOnePurchase,
    impressionTableBookingCartPage,
    clickConfirmBookingTable,
    impressionBillDetails,
    clickViewOneBenefits,
    impressionTicketDetails,
    clickTicketItem,
    clickDeleteTicketItem,
    impressionEventBillDetails,
    clickProceedCtaForEventBooking,
    impressionProceedCtaForEventBooking,
    screenViewBookingReview,
    clickTermsAndConditions,
    clickTor,
    impressionDisclaimerBanner,
    impressionBenefitCallout,
    impressionProceedCtaForTableBooking,
    impressionErrorHalfCard,
    clickErrorHalfCard,
    scanQrErrorPageImpressionEvent,
    scanQrErrorPageBackClickEvent,
    scanQrErrorPagePrimaryActionClickEvent,
    scanQRBottomPopupScanAgainClickEvent,
    scanQRBottomPopupScanAgainImpressionEvent,
    clickTermsAndConditionsForEventBooking,
    impressionCashBackEarned,
    impressionCashBackToggle,
    clickCashBackToggle,
    impressionCashBackBenefitCallout,
    impressionTurboPayButtonReservation,
    clickTurboPayButtonReservation,
    clickChangePaymentMethod,
    impressionSpecialRequest,
    clickSpecialRequest,
    impressionSpecialRequestHalfcard,
    clickSubmitSpecialRequest,
    clickDeleteSpecialRequest,
} from "@dineout-cart-client/analytics/events";
import { ITableBookingCartPage } from "@dineout-cart-client/analytics/events";
import {
    ACTION_TYPE,
    ACTIONS,
    CLICK_ACTIONS,
    COUPON_FAILIURE,
    COUPON_SUCCESS,
    IMPRESSION_ACTIONS,
    SCREEN_NAME,
} from "@dineout-cart-client/analytics/constants";
import {
    dinecashPoolingWidgetClickEvent,
    dinecashPoolingWidgetImpressionEvent,
    paymentCartClickEvent,
    paymentCartImpressionEvent,
} from "@dineout-cart-client/analytics/events/PaymentCart";
import { getBillEntriesList } from "@dineout-cart-client/utils/TableReservationUtils";

// analytics interfaces
import {
    IImpressionOnePurchase,
    IImpressionBillDetails,
    IImpressionTicketDetails,
    IClickTicketItem,
    IImpressionEventBillDetails,
    IClickEventProceed,
    IImpressionEventProceed,
    IScreenViewBookingReview,
    IClickTermsAndConditions,
    IClickTor,
    IImpressionDisclaimerBanner,
    IImpressionBenefitCallout,
    IImpressionProceedCtaForTableBooking,
    IErrorHalfCardEvent,
    IErrorPageImpressionEvent,
    IImpressionCashBackEarned,
    IImpressionCashBackToggle,
    IClickCashBackToggle,
    IImpressionCashBackBenefitCallout,
    onImpressionPaymentCartBillDetails,
    IImpressionDinecashPoolWidget,
    IImpressionPayWaiterCallout,
} from "@dineout-cart-client/interfaces/AnalyticsEvents";
import { ICouponStatus } from "@dineout-cart-client/interfaces/Coupon";

interface IIds {
    restaurantId: string;
    selectedOfferId: string;
    selectedSlotId: number;
}

export type IUpdateItemProp = IClickTicketItem;

export const useDineoutBookingAnalytics = () => {
    const { clickEvent, impressionEvent, screenViewEvent } = useContext(AnalyticsContext);

    const onImpressionViewTableBookingCart = useCallback(
        (ids: IIds, cartItem: IResponseCartItem, selectedDealCategory: string, billEntries: IBillEntries[]) => {
            const billDetailsEntry = getBillEntriesList(billEntries);
            impressionTableBookingCartPage(impressionEvent, {
                restaurantId: ids.restaurantId,
                selectedGuestCount: Number(cartItem?.guestCount),
                selectedDate: cartItem?.bookingDate,
                selectedSlot: cartItem?.bookingSlotTime,
                selectedOfferId: ids.selectedOfferId,
                selectedOfferText: cartItem?.ticketAttributes?.ticketTitle,
                selectedSlotId: ids.selectedSlotId,
                selectedDealCategory,
                billDetails: billDetailsEntry,
            });
        },
        [impressionEvent],
    );

    const onImpressionOnePurchase = useCallback(
        (subscriptionData: ISuperSavingBottomWidgetSubscriptionDataProps) => {
            const onePlan = subscriptionData?.planType || "";
            const duration = subscriptionData?.name || "";
            const price = String(subscriptionData?.finalPrice || "");

            impressionOnePurchase(impressionEvent, {
                onePlan,
                duration,
                price,
            } as IImpressionOnePurchase);
        },
        [impressionEvent],
    );

    const onClickConfirmBookingTableReviewPage = useCallback(
        (ids: IIds, cartItem: IResponseCartItem, selectedDealCategory: string, billEntries: IBillEntries[]) => {
            const billDetailsEntry = getBillEntriesList(billEntries);

            clickConfirmBookingTable(clickEvent, {
                restaurantId: ids.restaurantId,
                selectedGuestCount: Number(cartItem?.guestCount),
                selectedDate: cartItem?.bookingDate,
                selectedSlot: cartItem?.bookingSlotTime,
                selectedOfferId: ids.selectedOfferId,
                selectedOfferText: cartItem?.ticketAttributes?.ticketTitle,
                selectedSlotId: ids.selectedSlotId,
                selectedDealCategory,
                billDetails: billDetailsEntry,
            });
        },
        [clickEvent],
    );

    const onImpressionTurboPayButtonReservation = useCallback(
        (payload: ITableBookingCartPage) => {
            impressionTurboPayButtonReservation(impressionEvent, payload);
        },
        [impressionEvent],
    );

    const onClickTurboPayButtonReservation = useCallback(
        (payload: ITableBookingCartPage) => {
            clickTurboPayButtonReservation(clickEvent, payload);
        },
        [clickEvent],
    );

    const onClickChangePaymentMethod = useCallback(
        (payload: ITableBookingCartPage) => {
            clickChangePaymentMethod(clickEvent, payload);
        },
        [clickEvent],
    );

    const onClickCashBackToggle = useCallback(
        (payload: IClickCashBackToggle) => clickCashBackToggle(clickEvent, payload),
        [clickEvent],
    );

    const onImpressionCashBackBenefitCallout = useCallback(
        (prop: IImpressionCashBackBenefitCallout) => {
            const { bannerText, dealCategory, restaurantId } = prop;
            impressionCashBackBenefitCallout(impressionEvent, {
                bannerText,
                dealCategory,
                restaurantId,
            });
        },
        [impressionEvent],
    );

    const onImpressionBillDetails = useCallback(
        (prop: IOnImpressionBillDetailsProps) => {
            const {
                restaurantID,
                offerId,
                dealCategory,
                guests,
                slotDetails,
                billDetails,
                eventId,
                ticketDetails,
                isEventsBookingFlow,
            } = prop;

            if (isEventsBookingFlow) {
                impressionEventBillDetails(impressionEvent, {
                    restaurantID,
                    eventId: eventId || "",
                    guests,
                    billDetails,
                    ticketDetails: ticketDetails || {},
                } as IImpressionEventBillDetails);
            } else {
                impressionBillDetails(impressionEvent, {
                    restaurantID,
                    offerId,
                    dealCategory,
                    guests,
                    slotDetails,
                    billDetails,
                } as IImpressionBillDetails);
            }
        },
        [impressionEvent],
    );

    const onClickViewOneBenefits = useCallback(() => {
        clickViewOneBenefits(clickEvent);
    }, [clickEvent]);

    const onClickTermsAndConditionsForEventBooking = useCallback(
        (prop: IClickTermsAndConditions) => {
            const { restaurantId, guests, dealCategory } = prop;
            clickTermsAndConditionsForEventBooking(clickEvent, { restaurantId, guests, dealCategory });
        },
        [clickEvent],
    );

    const onImpressionTicketDetails = useCallback(
        (prop: IImpressionTicketDetails) => {
            const { restaurantId, eventId, tickets } = prop;
            impressionTicketDetails(impressionEvent, {
                restaurantId,
                eventId,
                tickets,
            });
        },
        [impressionEvent],
    );

    const onClickTicketItem = useCallback(
        (prop: IUpdateItemProp) => {
            const { restaurantId, eventId, ticketId, numberOfGuests, amount } = prop;
            clickTicketItem(clickEvent, { restaurantId, eventId, ticketId, numberOfGuests, amount });
        },
        [clickEvent],
    );

    const onClickDeleteTicketItem = useCallback(
        (prop: IUpdateItemProp) => {
            const { restaurantId, eventId, ticketId, numberOfGuests, amount } = prop;
            clickDeleteTicketItem(clickEvent, { restaurantId, eventId, ticketId, numberOfGuests, amount });
        },
        [clickEvent],
    );

    const onClickEventProceed = useCallback(
        (prop: IClickEventProceed) => {
            const { restaurantId, eventId, ticketDetails, dealCategory, guests, billDetails, buttonStatus } = prop;
            clickProceedCtaForEventBooking(clickEvent, {
                restaurantId,
                eventId,
                ticketDetails,
                dealCategory,
                guests,
                billDetails,
                buttonStatus,
            });
        },
        [clickEvent],
    );

    const onImpressionEventProceed = useCallback(
        (prop: IImpressionEventProceed) => {
            const { restaurantId, eventId, ticketDetails, dealCategory, guests, billDetails, buttonStatus } = prop;

            impressionProceedCtaForEventBooking(impressionEvent, {
                restaurantId: restaurantId || "",
                eventId: eventId || "",
                ticketDetails,
                dealCategory,
                guests,
                billDetails,
                buttonStatus,
            });
        },
        [impressionEvent],
    );

    const onImpressionCashBackEarned = useCallback(
        (prop: IImpressionCashBackEarned) => {
            const {
                eventId,
                dealCategory,
                restaurantId,
                billAmount,
                cashbackToBeEarned,
                cashbackPerc,
                isRestaurantHaving2xDineCash,
            } = prop;
            impressionCashBackEarned(impressionEvent, {
                eventId,
                dealCategory,
                restaurantId,
                billAmount,
                cashbackToBeEarned,
                cashbackPerc,
                isRestaurantHaving2xDineCash,
            });
        },
        [impressionEvent],
    );

    const onImpressionCashBackToggle = useCallback(
        (prop: IImpressionCashBackToggle) => {
            const {
                eventId,
                dealCategory,
                restaurantId,
                billAmount,
                cashbackToSpend,
                cashbackToEarn,
                toggleState,
                remainingBalance,
                cashbackPerc,
                couponApplied,
                couponDiscount,
                isRestaurantHaving2xDineCash,
            } = prop;
            impressionCashBackToggle(impressionEvent, {
                eventId,
                dealCategory,
                restaurantId,
                billAmount,
                cashbackToSpend,
                cashbackToEarn,
                toggleState,
                remainingBalance,
                cashbackPerc,
                couponApplied,
                couponDiscount,
                isRestaurantHaving2xDineCash,
            });
        },
        [impressionEvent],
    );

    const onImpressionProceedCtaForTableBooking = useCallback(
        (payload: IImpressionProceedCtaForTableBooking) => {
            impressionProceedCtaForTableBooking(impressionEvent, payload);
        },
        [impressionEvent],
    );

    const onImpressionErrorHalfCard = useCallback(
        (payload: IErrorHalfCardEvent) => {
            impressionErrorHalfCard(impressionEvent, payload);
        },
        [impressionEvent],
    );

    const onClickErrorHalfCard = useCallback(
        (payload: IErrorHalfCardEvent) => {
            clickErrorHalfCard(clickEvent, payload);
        },
        [clickEvent],
    );

    const onScreenViewBookingReview = useCallback(
        (prop: IScreenViewBookingReview) => {
            const { restaurantId, eventId, ticketId } = prop;
            screenViewBookingReview(screenViewEvent, {
                restaurantId: restaurantId || "",
                eventId: eventId || "",
                ticketId: ticketId || {},
            });
        },
        [screenViewEvent],
    );

    const onClickTermsAndConditions = useCallback(
        (payload: IClickTermsAndConditions) => {
            const { restaurantId, guests, dealCategory } = payload;
            clickTermsAndConditions(clickEvent, { restaurantId, guests, dealCategory });
        },
        [clickEvent],
    );

    const onClickTor = useCallback(
        (payload: IClickTor) => {
            const { restaurantId, guests, dealCategory } = payload;
            clickTor(clickEvent, { restaurantId, guests, dealCategory });
        },
        [clickEvent],
    );

    const onImpressionDisclaimerBanner = useCallback(
        (payload: IImpressionDisclaimerBanner) => {
            const { restaurantId, billAmount, source, dealId } = payload;
            impressionDisclaimerBanner(impressionEvent, { restaurantId, billAmount, source, dealId });
        },
        [impressionEvent],
    );

    const onImpressionBenefitCallout = useCallback(
        (payload: IImpressionBenefitCallout) => {
            const { offerText } = payload;
            impressionBenefitCallout(impressionEvent, { offerText });
        },
        [impressionEvent],
    );

    const onImpressionSpecialRequest = useCallback(
        (ids: IIds, cartItem: IResponseCartItem, selectedDealCategory: string, billEntries: IBillEntries[]) => {
            const billDetailsEntry = getBillEntriesList(billEntries);
            impressionSpecialRequest(impressionEvent, {
                restaurantId: ids.restaurantId,
                selectedGuestCount: Number(cartItem?.guestCount),
                selectedDate: cartItem?.bookingDate,
                selectedSlot: cartItem?.bookingSlotTime,
                selectedOfferText: cartItem?.ticketAttributes?.ticketTitle,
                selectedDealCategory,
                billDetails: billDetailsEntry,
            });
        },
        [impressionEvent],
    );

    const onClickSpecialRequest = useCallback(
        (ids: IIds, cartItem: IResponseCartItem, selectedDealCategory: string, billEntries: IBillEntries[]) => {
            const billDetailsEntry = getBillEntriesList(billEntries);
            clickSpecialRequest(clickEvent, {
                restaurantId: ids.restaurantId,
                selectedGuestCount: Number(cartItem?.guestCount),
                selectedDate: cartItem?.bookingDate,
                selectedSlot: cartItem?.bookingSlotTime,
                selectedOfferText: cartItem?.ticketAttributes?.ticketTitle,
                selectedDealCategory,
                billDetails: billDetailsEntry,
            });
        },
        [clickEvent],
    );

    const onImpressionSpecialRequestHalfcard = useCallback(
        (ids: IIds, cartItem: IResponseCartItem, selectedDealCategory: string, billEntries: IBillEntries[]) => {
            const billDetailsEntry = getBillEntriesList(billEntries);
            impressionSpecialRequestHalfcard(impressionEvent, {
                restaurantId: ids.restaurantId,
                selectedGuestCount: Number(cartItem?.guestCount),
                selectedDate: cartItem?.bookingDate,
                selectedSlot: cartItem?.bookingSlotTime,
                selectedOfferText: cartItem?.ticketAttributes?.ticketTitle,
                selectedDealCategory,
                billDetails: billDetailsEntry,
            });
        },
        [impressionEvent],
    );

    const onClickSubmitSpecialRequest = useCallback(
        (params: {
            ids: IIds;
            cartItem: IResponseCartItem;
            selectedDealCategory: string;
            billEntries: IBillEntries[];
            instruction: string;
        }) => {
            const { ids, cartItem, selectedDealCategory, billEntries, instruction } = params;
            const billDetailsEntry = getBillEntriesList(billEntries);
            clickSubmitSpecialRequest(clickEvent, {
                restaurantId: ids.restaurantId,
                selectedGuestCount: Number(cartItem?.guestCount),
                selectedDate: cartItem?.bookingDate,
                selectedSlot: cartItem?.bookingSlotTime,
                selectedOfferText: cartItem?.ticketAttributes?.ticketTitle,
                selectedDealCategory,
                billDetails: billDetailsEntry,
                instruction,
            });
        },
        [clickEvent],
    );

    const onClickDeleteSpecialRequest = useCallback(
        (ids: IIds, cartItem: IResponseCartItem, selectedDealCategory: string, billEntries: IBillEntries[]) => {
            const billDetailsEntry = getBillEntriesList(billEntries);
            clickDeleteSpecialRequest(clickEvent, {
                restaurantId: ids.restaurantId,
                selectedGuestCount: Number(cartItem?.guestCount),
                selectedDate: cartItem?.bookingDate,
                selectedSlot: cartItem?.bookingSlotTime,
                selectedOfferText: cartItem?.ticketAttributes?.ticketTitle,
                selectedDealCategory,
                billDetails: billDetailsEntry,
            });
        },
        [clickEvent],
    );

    return {
        onClickViewOneBenefits,
        onImpressionBillDetails,
        onImpressionOnePurchase,
        onImpressionViewTableBookingCart,
        onClickConfirmBookingTableReviewPage,
        onImpressionTicketDetails,
        onClickTicketItem,
        onClickDeleteTicketItem,
        onClickEventProceed,
        onImpressionEventProceed,
        onScreenViewBookingReview,
        onClickTermsAndConditions,
        onClickTermsAndConditionsForEventBooking,
        onClickTor,
        onImpressionDisclaimerBanner,
        onImpressionBenefitCallout,
        onImpressionProceedCtaForTableBooking,
        onImpressionErrorHalfCard,
        onClickErrorHalfCard,
        onImpressionCashBackEarned,
        onImpressionCashBackToggle,
        onClickCashBackToggle,
        onImpressionCashBackBenefitCallout,
        onImpressionTurboPayButtonReservation,
        onClickTurboPayButtonReservation,
        onClickChangePaymentMethod,
        onImpressionSpecialRequest,
        onClickSpecialRequest,
        onImpressionSpecialRequestHalfcard,
        onClickSubmitSpecialRequest,
        onClickDeleteSpecialRequest,
    };
};

export const useBillPaymentAnalytics = () => {
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);

    const onImpressionDinecashPoolWidget = useCallback(
        (prop: IImpressionDinecashPoolWidget) => {
            dinecashPoolingWidgetImpressionEvent({
                event: impressionEvent,
                category: SCREEN_NAME.PAYMENT_CART,
                action: IMPRESSION_ACTIONS.IMPRESSION_DINECASH_POOL_WIDGET,
                label: "",
                context: JSON.stringify(prop),
            });
        },
        [impressionEvent],
    );

    const onImpressionPayWaiterCallout = useCallback(
        (prop: IImpressionPayWaiterCallout) => {
            paymentCartImpressionEvent({
                event: impressionEvent,
                category: SCREEN_NAME.PAYMENT_CART,
                action: IMPRESSION_ACTIONS.PAY_WAITER_CALLOUT,
                label: "",
                context: JSON.stringify(prop),
            });
        },
        [impressionEvent],
    );

    const onClickDinecashPoolWidget = useCallback(
        (prop: IImpressionDinecashPoolWidget) => {
            dinecashPoolingWidgetClickEvent({
                event: clickEvent,
                category: SCREEN_NAME.PAYMENT_CART,
                action: CLICK_ACTIONS.CLICK_DINECASH_POOL_WIDGET,
                label: "",
                context: JSON.stringify(prop),
            });
        },
        [clickEvent],
    );

    const onImpressionPayButton = useCallback(
        (prop: onImpressionPaymentCartBillDetails) => {
            paymentCartImpressionEvent({
                event: impressionEvent,
                category: SCREEN_NAME.PAYMENT_CART,
                action: ACTIONS.PAY_BTN,
                label: ACTION_TYPE.PAY_BTN_VIEW,
                context: JSON.stringify(prop),
            });
        },
        [impressionEvent],
    );

    const onImpressionTurboCheckout = useCallback(
        (prop: IImpressionTurboCheckout) => {
            paymentCartImpressionEvent({
                event: impressionEvent,
                category: SCREEN_NAME.PAYMENT_CART,
                action: IMPRESSION_ACTIONS.IMPRESSION_TURBO_CHECKOUT,
                label: ACTION_TYPE.PAY_BTN_VIEW,
                context: JSON.stringify(prop),
            });
        },
        [impressionEvent],
    );

    const onClickTurboPaymentMethodChange = useCallback(
        (prop: IClickTurboPaymentMethodChange) => {
            paymentCartClickEvent({
                event: clickEvent,
                category: SCREEN_NAME.PAYMENT_CART,
                action: ACTIONS.CLICK_TURBO_PAYMENT_CHANGE,
                label: "",
                context: JSON.stringify(prop),
            });
        },
        [clickEvent],
    );

    const onClickTurboCheckout = useCallback(
        (prop: IClickTurboCheckout) => {
            paymentCartClickEvent({
                event: clickEvent,
                category: SCREEN_NAME.PAYMENT_CART,
                action: ACTIONS.CLICK_TURBO_CHECKOUT,
                label: ACTION_TYPE.PAY_BTN_CLICK,
                context: JSON.stringify(prop),
            });
        },
        [clickEvent],
    );

    const onImpressionDistanceAlert = useCallback(
        (prop: ImpressionDistanceAlert) => {
            paymentCartImpressionEvent({
                event: impressionEvent,
                category: SCREEN_NAME.PAYMENT_CART,
                action: ACTIONS.PAYMENT_LOCATION_ALERT,
                label: ACTION_TYPE.PAYMENT_LOCATION_ALERT_VIEWED,
                context: JSON.stringify(prop),
            });
        },
        [impressionEvent],
    );

    const onImpressionCoupon = useCallback(
        (prop: ICouponStatus) => {
            paymentCartImpressionEvent({
                event: impressionEvent,
                category: SCREEN_NAME.PAYMENT_CART,
                action: ACTIONS.PAYMENT_CART,
                label: ACTION_TYPE.COUPON_SAVINGS_POPUP,
                context: prop?.status ? COUPON_SUCCESS : COUPON_FAILIURE,
            });
        },
        [impressionEvent],
    );

    const onClickCouponEvent = useCallback(
        (prop: ICouponStatus) => {
            paymentCartClickEvent({
                event: clickEvent,
                category: SCREEN_NAME.PAYMENT_CART,
                action: ACTIONS.PAYMENT_CART,
                label: ACTION_TYPE.COUPON_SAVINGS_POPUP,
                context: prop?.status ? COUPON_SUCCESS : COUPON_FAILIURE,
            });
        },
        [clickEvent],
    );

    const onRemoveCouponClick = useCallback(
        (prop: ICoupon) => {
            paymentCartClickEvent({
                event: clickEvent,
                category: SCREEN_NAME.PAYMENT_CART,
                action: ACTIONS.PAYMENT_CART,
                label: ACTION_TYPE.COUPON_SAVINGS_POPUP,
                context: prop?.coupon || "",
            });
        },
        [clickEvent],
    );
    const onHelpClicked = useCallback(() => {
        paymentCartClickEvent({
            event: clickEvent,
            category: SCREEN_NAME.PAYMENT_CART,
            action: ACTIONS.HELP,
            label: ACTION_TYPE.HELP_CLICK,
            context: "",
        });
    }, [clickEvent]);

    const onGoBack = useCallback(() => {
        paymentCartClickEvent({
            event: clickEvent,
            category: SCREEN_NAME.PAYMENT_CART,
            action: ACTIONS.GO_BACK,
            label: ACTION_TYPE.BACK_CLICK,
            context: "",
        });
    }, [clickEvent]);

    const onClickProceedToPay = useCallback(
        (prop: IProceedtoPayClick) => {
            paymentCartClickEvent({
                event: clickEvent,
                category: SCREEN_NAME.PAYMENT_CART,
                action: ACTIONS.PAYMENT_CART,
                label: ACTION_TYPE.PAY_BTN_CLICK,
                context: JSON.stringify(prop),
            });
        },
        [clickEvent],
    );

    const onTipViewed = useCallback(() => {
        paymentCartImpressionEvent({
            event: impressionEvent,
            category: SCREEN_NAME.PAYMENT_CART,
            action: ACTIONS.TIPPING_WIDGET,
            label: ACTION_TYPE.TIPPING_WIDGET_VIEW,
            context: "",
        });
    }, [impressionEvent]);

    const onTipClicked = useCallback(() => {
        paymentCartClickEvent({
            event: clickEvent,
            category: SCREEN_NAME.PAYMENT_CART,
            action: ACTIONS.ADD_TIP,
            label: ACTION_TYPE.ADD_TIP_CLICK,
            context: "",
        });
    }, [clickEvent]);

    const onImpressionBillDetails = useCallback(
        (prop: IOnImpressionBillDetailsProps) => {
            const {
                restaurantID,
                offerId,
                dealCategory,
                guests,
                slotDetails,
                billDetails,
                paybillSource,
                paymentFlow,
            } = prop;
            impressionBillDetails(impressionEvent, {
                restaurantID,
                offerId,
                dealCategory,
                guests,
                slotDetails,
                billDetails,
                paybillSource,
                paymentFlow,
            });
        },
        [impressionEvent],
    );

    const onScanQrErrorPageImpression = useCallback(
        (prop: IErrorPageImpressionEvent) => {
            scanQrErrorPageImpressionEvent(impressionEvent, prop);
        },
        [impressionEvent],
    );

    const onScanQrErrorPageBackClick = useCallback(
        (prop: IErrorPageImpressionEvent) => {
            scanQrErrorPageBackClickEvent(clickEvent, prop);
        },
        [clickEvent],
    );

    const onScanQrErrorPagePrimaryActionClick = useCallback(
        (prop: IErrorPageImpressionEvent) => {
            scanQrErrorPagePrimaryActionClickEvent(clickEvent, prop);
        },
        [clickEvent],
    );

    const onScanQRBottomPopupScanAgainClick = useCallback(
        (prop: IErrorPageImpressionEvent) => {
            scanQRBottomPopupScanAgainClickEvent(clickEvent, prop);
        },
        [clickEvent],
    );

    const onScanQRBottomPopupScanAgainImpression = useCallback(
        (prop: IErrorPageImpressionEvent) => {
            scanQRBottomPopupScanAgainImpressionEvent(impressionEvent, prop);
        },
        [impressionEvent],
    );

    return {
        onImpressionPayButton,
        onImpressionDistanceAlert,
        onImpressionCoupon,
        onClickCouponEvent,
        onRemoveCouponClick,
        onClickProceedToPay,
        onImpressionBillDetails,
        onTipViewed,
        onTipClicked,
        onHelpClicked,
        onGoBack,
        onScanQrErrorPageImpression,
        onScanQrErrorPageBackClick,
        onScanQrErrorPagePrimaryActionClick,
        onScanQRBottomPopupScanAgainClick,
        onScanQRBottomPopupScanAgainImpression,
        onImpressionTurboCheckout,
        onClickTurboCheckout,
        onClickTurboPaymentMethodChange,
        onImpressionDinecashPoolWidget,
        onClickDinecashPoolWidget,
        onImpressionPayWaiterCallout,
    };
};
